// @flow
import type { AbstractComponent } from 'react';
import styled from '@emotion/styled';
import { typography, SPACING, FONT_WEIGHTS } from '@tablekit/theme';
import LazyLoad from 'react-lazyload';
import { LAPTOP_BREAKPOINT, LAZY_LOAD_OFFSET } from 'utils/theme';
import columnWidth from 'utils/columnWidth';
import type {
  BlokComponentPropsType,
  LargeCardImageType
} from 'components/types';
import CoreImage from 'components/Image';
import Link from 'components/Link';
import CardStyles, { FULL_TOP_IMAGE_CLASS } from 'styled/Card';

type PropsType = BlokComponentPropsType<LargeCardImageType> & {
  cardAlign: string,
  component?: AbstractComponent<*, *>
};

export const CARD_WIDTH = 340;

export const LargeImagesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: ${SPACING.L6} -${SPACING.L3} 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (min-width: ${CARD_WIDTH * 2}px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const CoreLargeImageCard = styled(CardStyles.withComponent(Link))`
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  width: 100%;
  margin: 0 0 ${SPACING.L5};
  max-width: ${CARD_WIDTH}px;
`;

const Image = styled(CoreImage)`
  width: 100%;
`;

const Content = styled.div`
  padding: ${SPACING.L4};
  text-align: left;
  max-width: 100%;
`;

const Title = styled.div`
  ${typography('L5')};
  font-weight: ${FONT_WEIGHTS.BOLD};
  max-height: 28px;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: ${SPACING.L1} 0;
  max-width: 100%;
`;

const Description = styled.div`
  ${typography('L3')};
  max-width: 100%;
`;

export function LargeImageCard({
  blok: { image, link, description, title },
  component: Component = CoreLargeImageCard,
  cardAlign,
  ...others
}: PropsType) {
  return (
    <Component link={link} {...others}>
      <LazyLoad offset={LAZY_LOAD_OFFSET}>
        <Image
          className={FULL_TOP_IMAGE_CLASS}
          src={image}
          alt={title || image}
          options={{ size: { width: CARD_WIDTH } }}
        />
      </LazyLoad>
      <Content>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </Content>
    </Component>
  );
}

const Positioner = styled('div', {
  shouldForwardProp: (prop) => prop !== 'cardAlign'
})`
  width: ${columnWidth(12)}%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0 ${SPACING.L3};
  align-self: stretch;

  @media (min-width: ${CARD_WIDTH * 2}px) {
    width: ${columnWidth(6)}%;
  }

  @media ${LAPTOP_BREAKPOINT} {
    justify-content: ${({ cardAlign }) => cardAlign};
    width: ${columnWidth(4)}%;
  }
`;

export function StaticLargeImageCard({
  blok,
  cardAlign,
  ...others
}: PropsType) {
  return (
    <Positioner cardAlign={cardAlign}>
      <LargeImageCard blok={blok} cardAlign={cardAlign} {...others} />
    </Positioner>
  );
}
